import { numberHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { createCustomFieldSchema } from 'pages/requestForm/containers/netsuite/components/NetSuiteLineCustomField/NetSuiteLineCustomField.schema';
import {
    isFieldMandatory,
    isFieldVisible,
    isMandatoryFieldEmpty,
} from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { useNetSuiteExpenseReportExpenseLinesFieldSettings } from 'shared/hooks/useNetSuiteFieldSettings';
import { z } from 'zod';

const addNullishByCondition = <Schema extends z.ZodSchema>(
    schema: Schema,
    condition?: boolean
): Schema | z.ZodOptional<z.ZodNullable<Schema>> => {
    if (condition) return schema.nullish();

    return schema;
};

const stringSchema = z.string({ message: '' });
const numberSchema = z.number({ message: '' });
const requiredNumberSchema = numberSchema.min(0, { message: '' });
const attachmentSchema = z.object({ id: stringSchema });

export const createCustomFieldsSchema = (
    customFields: domain.Field[] = [],
    workflowId?: string
): Record<string, z.ZodSchema> => {
    return customFields.reduce<Record<string, z.ZodSchema>>((acc, field) => {
        acc[field.id] = createCustomFieldSchema(field, workflowId);

        return acc;
    }, {});
};

export const createSchema = (props: {
    fieldsSettings: ReturnType<typeof useNetSuiteExpenseReportExpenseLinesFieldSettings>;
    availableCustomFields?: domain.Field[];
    workflowId?: string;
}) => {
    const { fieldsSettings, availableCustomFields: customFields = [], workflowId } = props;

    const lineSchema = z
        .object({
            refLineNumber: addNullishByCondition(
                numberSchema.min(1, { message: '' }),
                !isFieldVisible(fieldsSettings.refLineNumber)
            ),
            expenseDate: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.date)),
                !isFieldVisible(fieldsSettings.date) || !isFieldMandatory(fieldsSettings.date)
            ),
            categoryId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.category)),
                !isFieldVisible(fieldsSettings.category) || !isFieldMandatory(fieldsSettings.category)
            ),
            expenseAccountId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.account)),
                !isFieldVisible(fieldsSettings.account) || !isFieldMandatory(fieldsSettings.account)
            ),
            foreignAmount: addNullishByCondition(
                requiredNumberSchema.max(constants.netSuiteConstants.maxCustomNumericFieldAmount),
                !isFieldMandatory(fieldsSettings.foreignAmount) || !isFieldVisible(fieldsSettings.foreignAmount)
            ),
            currencyId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.currency)),
                !isFieldVisible(fieldsSettings.currency) || !isFieldMandatory(fieldsSettings.currency)
            ),
            exchangeRate: addNullishByCondition(
                requiredNumberSchema.max(constants.netSuiteConstants.exchangeRateMaxValue),
                !isFieldMandatory(fieldsSettings.exchangeRate) || !isFieldVisible(fieldsSettings.exchangeRate)
            ),
            amount: addNullishByCondition(
                requiredNumberSchema.max(constants.netSuiteConstants.maxTotalAmount),
                !isFieldVisible(fieldsSettings.amount) || !isFieldMandatory(fieldsSettings.amount)
            ),
            taxCodeId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.taxCode)),
                !isFieldVisible(fieldsSettings.taxCode) || !isFieldMandatory(fieldsSettings.taxCode)
            ),
            taxRate: addNullishByCondition(
                requiredNumberSchema,
                !isFieldMandatory(fieldsSettings.taxRate) || !isFieldVisible(fieldsSettings.taxRate)
            ),
            taxAmount: addNullishByCondition(
                requiredNumberSchema,
                !isFieldMandatory(fieldsSettings.taxAmount) || !isFieldVisible(fieldsSettings.taxAmount)
            ),
            grossAmount: addNullishByCondition(
                requiredNumberSchema.max(constants.netSuiteConstants.maxCustomNumericFieldAmount),
                !isFieldVisible(fieldsSettings.grossAmount) || !isFieldMandatory(fieldsSettings.grossAmount)
            ),
            memo: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.memo)),
                !isFieldVisible(fieldsSettings.memo) || !isFieldMandatory(fieldsSettings.memo)
            ),
            departmentId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.department)),
                !isFieldVisible(fieldsSettings.department) || !isFieldMandatory(fieldsSettings.department)
            ),
            classId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.class)),
                !isFieldVisible(fieldsSettings.class) || !isFieldMandatory(fieldsSettings.class)
            ),
            locationId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.location)),
                !isFieldVisible(fieldsSettings.location) || !isFieldMandatory(fieldsSettings.location)
            ),
            customerId: addNullishByCondition(
                stringSchema.refine((value) => !isMandatoryFieldEmpty(value, fieldsSettings.customer)),
                !isFieldVisible(fieldsSettings.customer) || !isFieldMandatory(fieldsSettings.customer)
            ),
            isBillable: z.boolean(),
            attachment: addNullishByCondition(
                z
                    .array(attachmentSchema)
                    .refine((value) => !isMandatoryFieldEmpty(value[0]?.id, fieldsSettings.attachment)),
                !isFieldVisible(fieldsSettings.attachment) || !isFieldMandatory(fieldsSettings.attachment)
            ),
            isReceipt: z.boolean(),
            isNonReimbursable: z.boolean(),
            isCorporateCreditCard: z.boolean(),
            customFields: z.object(createCustomFieldsSchema(customFields, workflowId)),
        })
        .superRefine((line, ctx) => {
            // Gross Amount extended validation
            if (isFieldVisible(fieldsSettings.grossAmount)) {
                if (
                    (line.taxCodeId !== null || isFieldMandatory(fieldsSettings.grossAmount)) &&
                    !numberHelpers.isNumber(line.grossAmount)
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['grossAmount'],
                    });
                }
            }
        });

    return z.object({
        lines: z.array(lineSchema),
    });
};
